import {
	Component,
	OnInit,
	Input,
	Inject,
	Output,
	EventEmitter,
	OnChanges,
	OnDestroy,
	SimpleChanges,
} from '@angular/core';
import { JwtHelper } from '../../../services/core/jwt-helper.service';
import { DataCarrierService } from '../../../services/core/data-carrier.service';
import { AuthService } from '../../../services/core/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Environment } from '../../../config/environment';
import { DOCUMENT } from '@angular/common';
import { MessagingService } from '../../../services/messaging/messaging.service';
import { HealthFinding } from '../../../beans/health-summary/health-summary.module.bean';
import { TutorialComponent } from '../../tutorial/tutorial.component';
import { MatDialog } from '@angular/material/dialog';
import { TutorialService } from '../../../services/tutorial/tutorial.service';
import { ProfileRelatedInfoBean } from '../../../beans/account/account-module.bean';
import { tutorialDisabled } from '../../../constants/app.constants';
import { USER_LEVEL } from '../../../constants/user.level.constants';
import { APP_USER_PERMISSIONS } from '../../../constants/app-user-permissions.constants';
import { NgxPermissionsService } from 'ngx-permissions';
import { AppointmentsService } from '../../../services/scheduling/appointment/appointments.service';
import { AssessmentVersion } from 'src/app/services/health-history/health-history.types';
import { takeUntil } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
import { AssessmentCheckService } from 'src/app/services/health-history/assessment-check.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ProfileService } from 'src/app/services/core/profile.service';
import { NavController, Platform } from '@ionic/angular';
/**
 * Header Component
 */
@Component({
	selector: 'app-ehe-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
	/** handling navbar triggers */
	navbarOpen = false;
	/** handling mega menu */
	browseAll = false;
	/** maintenance message */
	showMaintenanceMessage = '';
	/** exam result */
	examResults: HealthFinding;
	/** user permission list */
	userPermission: Array<string> = [];
	/** USER Level constants */
	USER_LEVEL = USER_LEVEL;
	/** MODULE Level constants */
	MODULE_LEVEL = APP_USER_PERMISSIONS;
	/** denote if biometricEligible or not */
	biometricEligible: boolean;
	/** denote if the page loaded */
	isLoaded: boolean;
	/** denote auth flag, helps to show different headers */
	@Input() auth: boolean = false;
	/** helps to show wellness header */
	@Input() wellness: boolean = false;
	/** To hide headers if user is from HA page */
	@Input() healthHistory: boolean = false;
	/** To show or hide phone number on header */
	@Input() showPhone: boolean = false;
	/** To show or hide mobile back button */
	@Input() showMobileBackButton: boolean = false;
	/** sharing profile related info to other components */
	@Output() profileRelatedInfo: EventEmitter<ProfileRelatedInfoBean>;
	/** profile related details */
	public profileRelatedInfoBean: ProfileRelatedInfoBean;
	/** user name */
	username: string;
	/** denote is matched member */
	isMatchedMember: boolean = false;
	/** tutorialDisabledFlag */
	tutorialDisabledFlag = tutorialDisabled;
	/** current URL */
	routerUrl: string;
	/** redirect URL */
	redirectUrl: string;
	/** Observable */
	private _destroyed$: Subject<boolean> = new Subject<boolean>();
	/** isSimplified */
	@Input() isSimplified: boolean = false;
	/** Total appointment count */
	appointmentCount: number;
	// Top navigation link titles
	navBookExam: string = 'Book My Exam';
	navMyHealth: string = 'My Health';
	navMyMentors: string = 'Coaching';
	navMyAppointments: string = 'My Appointments';
	navAccount: string = 'Account';
	navMessages: string = 'Messages';
	navHelp: string = 'Help & FAQ';
	navTour: string = 'Portal Tour';
	navMore: string = 'More';
	navSignOut: string = 'Sign Out';
	navPhoneNumber: string;
	@Input() hideLinks: boolean = false;
	hideMyMentorNavLink: boolean = false;

	constructor(
		@Inject(DOCUMENT) private document: Document,
		public appointmentService: AppointmentsService,
		private assessmentCheckService: AssessmentCheckService,
		private authService: AuthService,
		private dataCarrierService: DataCarrierService,
		private dialog: MatDialog,
		private environment: Environment,
		private jwtService: JwtHelper,
		private permissionsService: NgxPermissionsService,
		private router: Router,
		private tutorialService: TutorialService,
		public messagingService: MessagingService,
		private spinner: SpinnerComponent,
		public profileService: ProfileService,
		private activatedRoute: ActivatedRoute,
		private navController: NavController,
		private platform: Platform
	) {
		this.username = this.jwtService.getUsername();
		this.profileRelatedInfoBean = new ProfileRelatedInfoBean();
		this.profileRelatedInfo = new EventEmitter<ProfileRelatedInfoBean>();
		// handling maintenance page
		if (this.environment.getEnvironment().UNDER_MAINTENANCE === 'true') {
			this.router.navigate(['maintenance']);
		} else if (this.environment.getEnvironment().MAINTENANCE_MESSAGE.length > 0) {
			this.showMaintenanceMessage = this.environment.getEnvironment().MAINTENANCE_MESSAGE;
		}
		this.biometricEligible = true;
		this.isLoaded = false;
		this.routerUrl = router.url;
		this.redirectUrl = '/scheduling/create/appointment/v3';
	}

	/** On initialization */
	ngOnInit() {
		this.navPhoneNumber = this.navPhoneNumber ?? '888.672.8172';

		if (!this.isSimplified) {
			localStorage.setItem('isMatchedUser', 'true');
			this.fetchProfileInfo();
		}
		// Remove disable wrapper scroll every time
		this.document.body.classList.remove('disable-wrapper-scroll');
		// redirects to vax pass
		this.activatedRoute.queryParams.subscribe(params => {
			if (params.tab === 'vax-pass') {
				this.spinner.startLoader();
				timer(1000).subscribe(t => {
					this.router.navigate(['/vaxstatus'], {
						queryParams: { display: 'consent-form' },
					});
				});
				this.spinner.stopLoader();
			}
		});
	}

	/*** Life cycle hook - On Changes */
	ngOnChanges(changes: SimpleChanges) {
		const biometricValue = this.dataCarrierService.getChannel().getValue().biometricEligible;
		if (biometricValue !== undefined && biometricValue !== null) {
			this.biometricEligible = biometricValue;
		}
		// fetch profile info if auth changes
		if (changes['auth']) {
			this.fetchProfileInfo();
		}
	}

	fetchProfileInfo() {
		const epmsPID = this.jwtService.getEpmsId();
		if (this.auth && epmsPID) {
			this.getProfileInfo();
			this._getDataCarrierService();
		} else {
			this.profileRelatedInfoBean.noMatch = true;
			this.profileRelatedInfo.emit(this.profileRelatedInfoBean);
			const navBar = document.getElementById('main-menu-nav');
			if (navBar && navBar.classList) {
				navBar.classList.add('l-40');
			}
		}
	}

	/**
	 *
	 * Subscribes to the DataCarrierService to get the latest values
	 * these values are used when _loadBackgroundAsyncCalls is called
	 * @private
	 * @memberof HeaderComponent
	 */
	private _getDataCarrierService(): void {
		this.dataCarrierService
			.getChannel()
			.pipe(takeUntil(this._destroyed$))
			.subscribe(data => {
				const canLoadData = this._permissionCheck();
				if (canLoadData === true) {
					return this._loadBackgroundAsyncCalls(data);
				}
				return;
			});
	}

	/**
	 *
	 * Checks if the user has the correct permissions to load the
	 * data from _loadBackgroundAsyncCalls
	 * @private
	 * @returns {boolean}
	 * @memberof HeaderComponent
	 */
	private _permissionCheck(): boolean {
		if (this.userPermission.length > 0) {
			const { CLIENT_BIOMETRIC_NOT_ELIGIBLE, UNMATCHED } = USER_LEVEL;
			const hubbleNotEligibleUser = this.userPermission.includes(CLIENT_BIOMETRIC_NOT_ELIGIBLE);
			const unmatchedUser = this.userPermission.includes(UNMATCHED);
			const invalidPerms = hubbleNotEligibleUser || unmatchedUser;
			return invalidPerms === false;
		}
		return false;
	}

	/**
	 *
	 * Checks if the respective api data is found in the data carrier
	 * service if it's not then this function will call the respective
	 * method to retrive the latest data. This means that an async call
	 * is made and then as soon as we have the data we won't keep trying
	 * to get the data.
	 * @private
	 * @param {*} data
	 * @memberof HeaderComponent
	 */
	private _loadBackgroundAsyncCalls(data: any): void {
		const { assessmentVersionFormat }: { assessmentVersionFormat: boolean } = data;
		if (assessmentVersionFormat === undefined || assessmentVersionFormat === null) {
			this._getAssessmentVersion();
		}
	}

	/**
	 *
	 * retrieves the AssessmentVersion for the user and then sends the data
	 * to _assessmentFormatCheck
	 * @private
	 * @memberof HeaderComponent
	 */
	private _getAssessmentVersion(): void {
		this.assessmentCheckService
			.getAssessmentVersion()
			.pipe(takeUntil(this._destroyed$))
			.subscribe(
				(assessmentVersion: AssessmentVersion) => this._assessmentFormatCheck(assessmentVersion),
				(err: Response) => {
					// FIXME: determine error type
					console.error(`[${HeaderComponent.name}][${this._getAssessmentVersion.name}]`, err);
				}
			);
	}

	/**
	 *
	 * Checks for an old format of the health assessment and if it's an older
	 * version then it will add the pdf into the DataCarrierService so it can
	 * be used instead of the new health assessment form
	 * @private
	 * @param {AssessmentVersion} assessmentVersion
	 * @memberof HeaderComponent
	 */
	private _assessmentFormatCheck(assessmentVersion: AssessmentVersion): void {
		const { oldFormat } = assessmentVersion;
		this.dataCarrierService.pushValue('assessmentVersionFormat', oldFormat);
		if (oldFormat === true) {
			const epmsPID = this.jwtService.getEpmsId();
			this.dataCarrierService.pushValue('assessmentPdf', {
				id: 0,
				documentName: 'Health_Assessment',
				epmsPID: epmsPID,
			});
		}
	}

	/** get profile information from service along with its
	 *  permissions for the application
	 * */
	getProfileInfo() {
		this.profileService.getProfileInfo().subscribe({
			next: (res: ProfileRelatedInfoBean) => {
				this.profileRelatedInfoBean = res;
				this.setProfileInfo();
				this.dataCarrierService.pushValue('profile', this.profileRelatedInfoBean);
			},
			error: err => {
				this.spinner.stopLoader();
			},
		});
	}

	setBasicContext() {
		this.profileService.setFirstName(this.profileRelatedInfoBean.profileInfo.firstName);
		this.profileService.setLastName(this.profileRelatedInfoBean.profileInfo.lastName);
		this.profileService.setGender(this.profileRelatedInfoBean.profileInfo.gender);
		this.isMatchedMember = this.profileRelatedInfoBean.profileInfo.userMatched;
		if (!this.isMatchedMember) {
			const navBar = document.getElementById('main-menu-nav');
			if (navBar && navBar.classList) {
				navBar.classList.add('l-40');
			}
		}
		localStorage.setItem('isMatchedUser', '' + this.isMatchedMember);
		if (this.userPermission.length === 0) {
			this.setPermission();
		}
		this.profileRelatedInfo.emit(this.profileRelatedInfoBean);
	}

	setProfileInfo() {
		this.setBasicContext();

		this.messagingService.setCurrentMessageCount({
			inbox: this.profileRelatedInfoBean.unreadMessageCount,
		});
		this.appointmentService.setUpcomingAppointmentCount(this.profileRelatedInfoBean.confirmedAppointmentCount);
	}

	/**
	 * logout the user from application
	 * clearing the storage and AWS Credentials
	 */
	logout() {
		this.authService.logoutUser();
		this.navController.navigateRoot(['/login'], { queryParams: { clear: true } });
	}

	/** Toggle mega menu */
	toggleBrowseAll() {
		this.browseAll = !this.browseAll;
	}

	/** On Mobile devices toggling navbar */
	toggleNavbar() {
		this.navbarOpen = !this.navbarOpen;
		this.document.body.classList.toggle('disable-wrapper-scroll');
	}

	/** Handling tutorial modal */
	openTutorialModal() {
		const dialogRef = this.dialog.open(TutorialComponent, {
			disableClose: true,
			backdropClass: 'tutorial-backdrop',
		});

		dialogRef.afterClosed().subscribe(result => {
			const postBody = { status: result };
			this.tutorialService.postStatus(postBody);
		});
	}

	/** Setting user permission based on flags */
	setPermission() {
		this.isLoaded = true;
		const { profileRelatedInfoBean } = this;
		const { profileInfo } = profileRelatedInfoBean;
		const { userMatched, client, eligibleForPE } = profileInfo;
		const { CLIENT_BIOMETRIC_ELIGIBLE, CLIENT_BIOMETRIC_NOT_ELIGIBLE, MATCHED, UNMATCHED, CLIENT_HIDE_HM } = USER_LEVEL;
		if (userMatched) {
			if (client.isSsn) {
				this.dataCarrierService.pushValue('biometricUser', true);
				this.dataCarrierService.pushValue('eligibleForPE', eligibleForPE);
				if (eligibleForPE) {
					this.biometricEligible = true;
					this.dataCarrierService.pushValue('biometricEligible', this.biometricEligible);
					this.userPermission.push(CLIENT_BIOMETRIC_ELIGIBLE);
				} else {
					this.biometricEligible = false;
					this.dataCarrierService.pushValue('biometricEligible', this.biometricEligible);
					this.userPermission.push(CLIENT_BIOMETRIC_NOT_ELIGIBLE);
				}
			} else {
				this.biometricEligible = true;
				this.userPermission.push(MATCHED);
				if (client.hideMentor) {
					this.userPermission.push(CLIENT_HIDE_HM);
					this.hideMyMentorNavLink = true;
				}
			}
		} else {
			this.biometricEligible = true;
			this.userPermission.push(UNMATCHED);
		}
		// need to keep the user level permissions in sync with app level permissions
		const appLevelPermissions = this.authService.getAppPermissionsForLoggedInUser() || [];
		this.permissionsService.loadPermissions([...this.userPermission, ...appLevelPermissions]);
	}

	ngOnDestroy(): void {
		this._destroyed$.next(true);
		this._destroyed$.unsubscribe();
	}

	getLastName() {
		if (
			this.profileRelatedInfoBean &&
			this.profileRelatedInfoBean.profileInfo &&
			this.profileRelatedInfoBean.profileInfo.lastName
		) {
			return this.profileRelatedInfoBean.profileInfo.lastName.charAt(0).toUpperCase();
		}
	}

	getUpcomingApptCount() {
		const upcomingCount = this.appointmentService.getUpcomingAppointmentsCount().value;

		if (upcomingCount) {
			this.appointmentCount = upcomingCount;
		} else {
			this.appointmentCount = 0;
		}

		return this.appointmentCount;
	}

	/**
	 * Handle back button click on mobile
	 * @param event - Click event
	 */
	goBack(event: Event) {
		event.preventDefault();
		event.stopPropagation();

		// Use Ionic NavController to go back
		this.navController.back();
	}

	checkMobileBackButtonVisibility() {
		return this.showMobileBackButton && this.platform.is('mobile');
	}
}
